/**
 * MCP connector consent (ENG-74).
 *
 * The authorization endpoint we advertise in our OAuth metadata is
 * `/oauth/authorize`, which lands here. The browser arrives carrying the OAuth
 * parameters; this page asks the backend what they mean, shows the user, and on
 * approval posts them back for a one-time code — then follows the redirect the
 * server hands back.
 *
 * WHY THE ACCOUNT ISN'T A PICKER
 * The account is already fixed by the connector URL the admin set up, and it
 * travels here in the RFC 8707 `resource` parameter. There is nothing to choose,
 * and so no way to authorize the wrong client by leaving a default alone. The
 * user's own session may be on a different account entirely — that's fine and
 * deliberately untouched; we never switch it.
 *
 * The audience is an analyst at a customer, not an operator. No OAuth vocabulary
 * reaches the screen: no scopes, no client ids, no talk of tokens.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiService from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { FeatureFlag, useFeatureFlagResolved } from '@/services/featureFlag.service';
import McpDisabledNotice from '@/components/McpDisabledNotice';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { usePageBranding } from '@/hooks/usePageBranding';
import pliableLogo from '../assets/images/pliable/logo_with_text_black.png';

type Grant = { scope: string; description: string };

type Branding = {
  logo_path?: string | null;
  page_title?: string | null;
  favicon_path?: string | null;
};

type AuthorizeContext = {
  account_id: string;
  account_name: string;
  client_name: string;
  resource: string;
  grants: Grant[];
  // Of the account being authorized, which is not necessarily the one the
  // browser session is currently on.
  branding?: Branding;
  brand_name?: string;
};

export default function McpConsent() {
  const [params] = useSearchParams();

  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const resource = params.get('resource') || '';
  const scope = params.get('scope') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || '';

  const [context, setContext] = useState<AuthorizeContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Wait for auth before asking. Claude opens this page in a fresh tab, where
  // sessionStorage isn't shared and the provider hasn't rehydrated yet — a
  // fetch on mount goes out with no Authorization header and 401s, which the
  // user sees as an unexplained "can't connect".
  // Gate on what ApiService will actually send, not on the auth context. Those
  // two disagree on a cold load — the context has a user before the token
  // reaches the singleton ApiService reads — which is why this 401'd on direct
  // navigation but worked when reached from inside the SPA.
  // Gate on what ApiService will actually send, not on the auth context. The two
  // disagree on a cold load — the context has a user before the token reaches
  // the singleton ApiService reads — which is why this 401'd on direct
  // navigation and worked when reached from inside the SPA.
  const apiReady = useApiAuthReady();

  // Resolved variant so a cold load waits rather than telling someone
  // mid-authorization that their account can't connect, only to change its mind.
  const { value: mcpEnabled, isLoading: flagLoading } = useFeatureFlagResolved(
    FeatureFlag.ALLOW_MCP,
    false,
  );

  useEffect(() => {
    // Don't even ask what's being authorized if the feature is off — the answer
    // can't be acted on.
    if (!apiReady || !mcpEnabled) return;
    // Everything is validated server-side before we render — wrong account, an
    // unregistered redirect, a role that can't connect. Finding out here means
    // the user is told before they click Approve rather than after.
    // ApiService serializes a GET payload into the query string itself.
    ApiService.getInstance()
      .request('GET', '/mcp/oauth/authorize-context', {
        resource,
        client_id: clientId,
        redirect_uri: redirectUri,
        scope,
      })
      .then((data: any) => {
        if (data?.error) setError(data.error_description || 'This request could not be authorized.');
        else setContext(data as AuthorizeContext);
      })
      .catch((e: any) => setError(describe(e, 'Could not load this authorization request.')));
  }, [resource, clientId, redirectUri, scope, apiReady, mcpEnabled]);

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = (await ApiService.getInstance().request('POST', '/mcp/oauth/consent', {
        resource,
        client_id: clientId,
        redirect_uri: redirectUri,
        scope,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod,
      })) as { redirect_to?: string; error?: string; error_description?: string };

      if (resp.error || !resp.redirect_to) {
        setBusy(false);
        setError(resp.error_description || 'This request could not be authorized.');
        return;
      }
      // The server built this, including the `iss` parameter clients require.
      window.location.href = resp.redirect_to;
    } catch (e: any) {
      setBusy(false);
      setError(describe(e, 'Could not complete authorization.'));
    }
  };

  const branding = context?.branding;
  const logoPath = branding?.logo_path || pliableLogo;
  const brandName = context?.brand_name || 'Pliable';

  // Tab title and favicon follow the same branding, so a whitelabeled tenant's
  // user never sees our name in the browser chrome either.
  usePageBranding(branding);

  if (flagLoading) {
    return <Centered><span className="text-sm text-muted-foreground">Loading…</span></Centered>;
  }

  // Ahead of the error and loading branches: someone on an account without the
  // feature should get the explanation, never a connector error.
  //
  // NOTE: the flag is evaluated for the SIGNED-IN user's own account, while the
  // account being authorized comes from the OAuth `resource` parameter. Those
  // are the same in every ordinary flow — you authorize the account you belong
  // to and are signed into — but they can differ, and this gate follows the
  // session. Enforcing it for the target account means checking server-side,
  // where the target is already resolved.
  if (!mcpEnabled) {
    return (
      <Centered>
        <div className="w-full max-w-md rounded-lg border bg-card shadow-sm">
          <McpDisabledNotice context="authorize" accountName={context?.account_name} />
        </div>
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 text-left shadow-sm">
          <h1 className="text-lg font-semibold">Can't connect this account</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => window.close()}>
              Close
            </Button>
          </div>
        </div>
      </Centered>
    );
  }

  if (!context) {
    return <Centered><span className="text-sm text-muted-foreground">Loading…</span></Centered>;
  }

  return (
    <Centered>
      <div className="w-full max-w-md space-y-6 rounded-lg border bg-card p-6 text-left shadow-sm">
        <div className="space-y-2">
          {/* The tenant's own mark when whitelabeled — this page is the first
              thing an end user sees, and it must look like the product they
              actually use. Falls back to ours. */}
          <img src={logoPath} alt={brandName} className="h-8" />
          {/* The account is stated as a fact, and prominently: it is the one
              thing the user must not get wrong. */}
          <h1 className="text-xl font-semibold leading-tight">
            You are authorizing <span className="whitespace-nowrap">{context.account_name}</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            {context.client_name} is asking to work with your{' '}
            <span className="font-medium text-foreground">{context.account_name}</span> data.
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-sm font-medium">This will let it</span>
          <ul className="space-y-2">
            {context.grants.map((g) => (
              <li key={g.scope} className="flex items-start gap-2 text-sm text-muted-foreground">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                <span>{g.description}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-muted-foreground">
          It sees only what you can see — the same metrics and rows your {brandName} account
          allows. You can disconnect it at any time from your {brandName} settings.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => window.close()} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={approve} disabled={busy}>
            {busy ? 'Connecting…' : `Authorize ${context.account_name}`}
          </Button>
        </div>
      </div>
    </Centered>
  );
}

/**
 * Pull the reason out of an ApiService failure.
 *
 * ApiService throws ApiError('API Error', status, body) on any non-2xx, so
 * `e.message` is always the literal string "API Error" — the OAuth
 * `error_description` we carefully write server-side is sitting in the body,
 * unread. This screen is where someone finds out why they can't connect, so it
 * has to show the real reason.
 */
function describe(e: any, fallback: string): string {
  const body = e?.payload;   // ApiError stores the parsed body here
  // OAuth errors use error_description/error; the rest of our API uses msg.
  return body?.error_description || body?.error || body?.msg || fallback;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6 text-center">{children}</div>
  );
}
